import { ReactNode } from "react";

import { useUser } from "@/hooks/useUser";

// Chat-specific providers (order matters!)
import { NavigatorProvider } from "@/contexts/navigator";
import { IntegrationsProvider } from "@/contexts/integrations";
import { ModalManagerProvider } from "@/contexts/modalManager";
import { WorkspaceLibraryProvider } from "@/contexts/workspaceLibContext";
import { AgentLibraryProvider } from "@/contexts/agentLibContext";
import { TaskLibraryProvider } from "@/contexts/taskLibraryContext";
import { AgentRegistryProvider } from "@/contexts/agentRegistryContext";
import { PreviewBrowserProvider } from "@/components/previewBrowser/PreviewBrowserContext";

// Service-cache invalidation (member events → shared member cache)
import { WorkspaceMembersCacheInvalidation } from "@/hooks/workspaceMembersCacheInvalidation";

// Our context providers
import { NavProvider } from "../contexts/navContext";
import { TabsProvider } from "../contexts/tabsContext";
import { ContextPanelProvider } from "../contexts/contextPanelContext";

// ============================================================================
// RE-EXPORTS
// ============================================================================

// Nav context (left panel)
export { NavProvider, useNav } from "../contexts/navContext";
export type { NavContextType } from "../contexts/navContext";

// Context panel (right sidebar)
export { ContextPanelProvider, useContextPanel } from "@/contexts/contextPanelContext";
export type { ContextPanelContextType } from "@/contexts/contextPanelContext";

// ChatView context (per-tab identity)
export { ChatViewProvider, useChatView } from "@/contexts/chatViewContext";
export type { ChatViewContextType } from "@/contexts/chatViewContext";

// Workspace members context (reactive member list)
export { WorkspaceMembersProvider, useChatViewMembers } from "@/contexts/workspaceMembersContext";
export type { WorkspaceMembersContextType } from "@/contexts/workspaceMembersContext";

// ============================================================================
// COMBINED PROVIDER
// ============================================================================

type Chat2ProviderProps = {
  children: ReactNode;
};

/**
 * Chat-specific providers for the main chat layout.
 *
 * Note: Core providers (AppSession, SocketIO, Permissions, Subscriptions,
 * Notifications, DataStreamEventBus) are provided by CoreProviders in App.tsx.
 *
 * This provider wraps chat-specific functionality:
 * - NavigatorProvider (navigation routing within chat)
 * - IntegrationsProvider (integrations panel functionality)
 * - ModalManagerProvider (modal/dialog management)
 * - NavProvider (left nav panel state)
 * - TabsProvider (rc-dock tabs + URL sync)
 * - WorkspaceLibraryProvider (workspace search/list - needs TabsProvider)
 * - AgentLibraryProvider (agent search/list - needs TabsProvider)
 * - PreviewBrowserProvider (per-workspace preview browser tabs - needs TabsProvider + NavigatorProvider)
 * - ContextPanelProvider (right contextual panel)
 *
 * Note: DndProvider is NOT included - rc-dock handles its own drag-drop.
 */
export function Chat2Provider({ children }: Chat2ProviderProps) {
  // Wait for user to be available before rendering providers
  // This is a latch - once true, stays true even if user disconnects
  const [, , , userReady] = useUser();
  
  if (!userReady) {
    return null; // CoreProviders' AppLoadingOverlay handles the loading UI
  }

  return (
    <NavigatorProvider>
      <IntegrationsProvider>
        <ModalManagerProvider>
          <NavProvider>
            <TabsProvider>
              <WorkspaceLibraryProvider>
                <AgentRegistryProvider>
                  <AgentLibraryProvider>
                    <TaskLibraryProvider>
                      <WorkspaceMembersCacheInvalidation>
                        <PreviewBrowserProvider>
                          <ContextPanelProvider>
                            {children}
                          </ContextPanelProvider>
                        </PreviewBrowserProvider>
                      </WorkspaceMembersCacheInvalidation>
                    </TaskLibraryProvider>
                  </AgentLibraryProvider>
                </AgentRegistryProvider>
              </WorkspaceLibraryProvider>
            </TabsProvider>
          </NavProvider>
        </ModalManagerProvider>
      </IntegrationsProvider>
    </NavigatorProvider>
  );
}
